import React, { useContext } from "react";
import {
  Box,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation,
  PAGE_ROUTE_BLOG
} from "@kivunova/kivufrontendcommon";
import { ACCESS_ROLE_ROWS } from "./constants/accessManagementContent";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import {
  publicInfoBodySx,
  publicInfoLinkSx,
  publicInfoSectionPanelSx,
  publicInfoSectionStackSx,
  publicInfoSectionTitleSx
} from "./constants/publicInfoPageStyles";

const AccessManagementPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const userGuidePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_USER_GUIDE, language, defaultLang)
      : `/${PAGE_ROUTE_USER_GUIDE}`;
  const contactPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_CONTACT, language, defaultLang)
      : `/${PAGE_ROUTE_CONTACT}`;
  const learnMorePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_BLOG, language, defaultLang)
      : `/${PAGE_ROUTE_BLOG}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="roles-access-page"
        eyebrow={t("roles-access-page-eyebrow", "Roles & permissions")}
        title={t(
          "roles-access-page-title",
          "How access is managed in Kivunova FMS"
        )}
        intro={t(
          "roles-access-page-intro",
          "Understand company-wide and store-level access, what each role can do, and who can invite employees."
        )}
      />

      <Stack sx={publicInfoSectionStackSx}>
        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t("roles-access-page-levels-heading", "Two levels of access")}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            {t(
              "roles-access-page-levels-intro",
              "Kivunova FMS uses two main levels of access: company level and store level."
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            <Box
              component="span"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {t(
                "roles-access-page-company-level-label",
                "Company-level access"
              )}
            </Box>{" "}
            {t(
              "roles-access-page-company-level-body",
              "gives a user access across the entire business, including all stores or branches."
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            <Box
              component="span"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {t("roles-access-page-store-level-label", "Store-level access")}
            </Box>{" "}
            {t(
              "roles-access-page-store-level-body",
              "limits a user to one assigned store. This means they can only access inventory, sales, orders, purchases, and other records related to that specific location."
            )}
          </Typography>
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "roles-access-page-access-rule",
              "A user's access is determined by two things: their role and the store they are assigned to. If a user does not have permission for a screen, button, or action, it will not be available to them in the app."
            )}
          </Typography>
        </Box>

        <Box>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t("roles-access-page-roles-heading", "Roles and responsibilities")}
          </Typography>
          <TableContainer
            sx={{
              borderRadius: 2,
              border: (theme) =>
                `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
              overflowX: "auto"
            }}
            data-testid="roles-access-page-table"
          >
            <Table size="small" sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, width: "32%" }}>
                    {t("roles-access-page-table-role-col", "Role")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("roles-access-page-table-desc-col", "Who it is for")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ACCESS_ROLE_ROWS.map((row) => (
                  <TableRow
                    key={row.id}
                    data-testid={`roles-access-page-row-${row.id}`}
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:nth-of-type(even)": {
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.03)
                      }
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        verticalAlign: "top"
                      }}
                    >
                      {t(row.roleKey, row.roleFallback)}
                    </TableCell>
                    <TableCell
                      sx={{ ...publicInfoBodySx, verticalAlign: "top" }}
                    >
                      {t(row.descriptionKey, row.descriptionFallback)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t(
              "roles-access-page-employees-heading",
              "Who can add or manage employees"
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            {t(
              "roles-access-page-employees-rule",
              "Kivunova FMS follows a simple access rule: a user can only assign roles below their own role level."
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            {t(
              "roles-access-page-employees-company-admin",
              "A Company Admin can invite and manage all other users and roles."
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            {t(
              "roles-access-page-employees-store-admin",
              "A Store Admin can add and manage store-level staff, such as warehouse managers, inventory clerks, and analysts. However, a Store Admin cannot create or manage another Company Admin."
            )}
          </Typography>
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "roles-access-page-employees-lower-roles",
              "Lower-level roles cannot add, invite, or manage employees."
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mt: 1.5 }}>
            {t(
              "roles-access-page-employees-closing",
              "This structure helps businesses stay secure, organized, and in control while ensuring each employee only accesses the information and actions needed for their job."
            )}
          </Typography>
        </Box>

        <PublicInfoCtaPanel
          title={t(
            "roles-access-page-cta-title",
            "Need help setting up your team?"
          )}
          testId="roles-access-page-cta"
        >
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "roles-access-page-cta-body",
              "Follow the User guide to invite employees, or contact us if you want help choosing the right roles."
            )}{" "}
            <MuiLink
              component={Link}
              to={userGuidePath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("roles-access-page-cta-guide-link", "User guide")}
            </MuiLink>
            {t("roles-access-page-cta-mid", " · ")}{" "}
            <MuiLink
              component={Link}
              to={contactPath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("roles-access-page-cta-contact-link", "Contact us")}
            </MuiLink>
            {t("roles-access-page-cta-mid", " · ")}{" "}
            <MuiLink
              component={Link}
              to={learnMorePath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("roles-access-page-cta-learn-more-link", "Learn more")}
            </MuiLink>
          </Typography>
        </PublicInfoCtaPanel>
      </Stack>
    </PublicMarketingLayout>
  );
};

export default AccessManagementPage;
